import { useWorkflowQuery, useWorkflowMutation, useWorkflowCompletion } from "./react.tsx";
import { workflowControlBusyKey } from "./mutation-model.ts";
import { Button, DropdownMenu } from "@cloudflare/kumo";
import {
  ArrowClockwiseIcon,
  ArrowsClockwiseIcon,
  CaretUpDownIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ListIcon,
  PaperPlaneTiltIcon,
  PauseIcon,
  PlayIcon,
  SpinnerIcon,
  SquareIcon,
  StopIcon,
  WarningCircleIcon
} from "@phosphor-icons/react";
import {
  memo,
  useEffect,
  useState,
  type MouseEvent
} from "react";
import { CreateWorkflowInstanceDialog } from "./CreateWorkflowInstanceDialog";
import {
  getAvailableActions,
  timeAgo,
  type WorkflowUiAction
} from "./workflow-format";
import { WorkflowSendEventDialog } from "./WorkflowSendEventDialog";
import { WorkflowStatusBadge } from "./WorkflowStatusBadge";
import {
  type WorkflowDefinitionRecord,
  type WorkflowInstanceRecord
} from "../../api/workflows/model.ts";

// Adapted from workers-sdk's local-explorer-ui workflow instances view:
// status summary strip, status filter, and instance rows with inline
// actions. Instance deletion has no server-side counterpart here, so the
// upstream delete flows are omitted.

interface WorkflowInstancesSurfaceProps {
  workspaceId: string;
  definition: WorkflowDefinitionRecord;
  active: boolean;
  onOpenInstance: (instance: WorkflowInstanceRecord) => void;
}


const STATUS_SUMMARY_CONFIG = [
  {
    key: "complete",
    label: "Complete",
    icon: CheckCircleIcon,
    color: "text-kumo-link",
    weight: "fill" as const
  },
  {
    key: "errored",
    label: "Errored",
    icon: WarningCircleIcon,
    color: "text-kumo-danger",
    weight: "fill" as const
  },
  {
    key: "queued",
    label: "Queued",
    icon: CircleNotchIcon,
    color: "text-kumo-subtle",
    weight: "regular" as const
  },
  {
    key: "running",
    label: "Running",
    icon: PlayIcon,
    color: "text-kumo-subtle",
    weight: "fill" as const
  },
  {
    key: "paused",
    label: "Paused",
    icon: PauseIcon,
    color: "text-kumo-subtle",
    weight: "fill" as const
  },
  {
    key: "waiting",
    label: "Waiting",
    icon: SpinnerIcon,
    color: "text-kumo-subtle",
    weight: "regular" as const
  },
  {
    key: "terminated",
    label: "Terminated",
    icon: SquareIcon,
    color: "text-kumo-subtle",
    weight: "fill" as const
  }
] as const;

const StatusSummary = memo(function StatusSummary({
  statusCounts
}: {
  statusCounts: Record<string, number>;
}) {
  return (
    <div className="mb-4 flex divide-x divide-kumo-fill overflow-hidden rounded-lg border border-kumo-fill bg-kumo-base">
      {STATUS_SUMMARY_CONFIG.map(({ key, label, icon: Icon, color, weight }) => (
        <div className="flex-1 space-y-1 px-4.5 pt-4 pb-3.5" key={key}>
          <div className="flex items-center gap-1.5 text-xs text-kumo-subtle">
            <Icon className={color} size={12} weight={weight} />
            {label}
          </div>
          <div className="mt-0.5 text-base text-kumo-default">
            {statusCounts[key] ?? 0}
          </div>
        </div>
      ))}
    </div>
  );
});

const ACTION_CONFIG: Record<
  WorkflowUiAction,
  { icon: typeof PauseIcon; style: string; weight: "fill" | "regular" }
> = {
  pause: {
    icon: PauseIcon,
    style: "border-kumo-fill bg-kumo-base text-kumo-default hover:bg-kumo-fill",
    weight: "fill"
  },
  resume: {
    icon: PlayIcon,
    style: "border-kumo-fill bg-kumo-base text-kumo-default hover:bg-kumo-fill",
    weight: "fill"
  },
  restart: {
    icon: ArrowClockwiseIcon,
    style: "border-kumo-fill bg-kumo-base text-kumo-default hover:bg-kumo-fill",
    weight: "regular"
  },
  terminate: {
    icon: StopIcon,
    style:
      "border-kumo-fill bg-kumo-base text-kumo-danger hover:bg-kumo-badge-red/10",
    weight: "fill"
  }
};

const InstanceRow = memo(function InstanceRow({
  instance,
  workspaceId,
  onActionComplete,
  onOpen,
  onSendEvent,
  ownerKey, active
}: {
  instance: WorkflowInstanceRecord;
  workspaceId: string;
  onActionComplete: () => void;
  onOpen: () => void;
  onSendEvent: () => void;
  ownerKey: string;
  active: boolean;
}) {
  const selectionKey = JSON.stringify([ownerKey, instance.id]);
  const mutation = useWorkflowMutation(selectionKey, active, workflowControlBusyKey(workspaceId, instance.id));
  const actionInProgress = mutation.snapshot.operation, mutating = mutation.busy;
  useWorkflowCompletion(mutation, () => onActionComplete());
  const actions = getAvailableActions(instance.status);
  const isTerminal =
    instance.status === "complete" ||
    instance.status === "errored" ||
    instance.status === "terminated";

  function handleAction(e: MouseEvent, action: WorkflowUiAction): void { e.stopPropagation();mutation.dispatch({ _tag: "Control", selectionKey, target: { workspaceId, instanceId: instance.id }, control: { action } }); }

  return (
    <div className="border-b border-kumo-fill bg-kumo-base p-1 last:border-b-0">
      <div
        className="grid h-10 cursor-pointer grid-cols-[100px_80px_1fr_auto] items-center gap-3 rounded-lg px-2 transition-colors hover:bg-kumo-fill"
        onClick={onOpen}
      >
        <div>
          <WorkflowStatusBadge status={instance.status} />
        </div>
        <span className="text-sm text-kumo-subtle">
          {timeAgo(instance.createdAt) || "—"}
        </span>
        <span className="truncate font-mono text-xs text-kumo-default">
          {instance.id}
        </span>
        <div className="flex items-center gap-1">
          {actions.map((action) => {
            const config = ACTION_CONFIG[action];
            const Icon = config.icon;
            return (
              <button
                key={action}
                className={`inline-flex size-7 cursor-pointer items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${config.style}`}
                disabled={mutating || actionInProgress !== null || !mutation.ready}
                aria-busy={!mutation.ready}
                onClick={(e) => void handleAction(e, action)}
                title={action.charAt(0).toUpperCase() + action.slice(1)}
                type="button"
              >
                <Icon
                  size={14}
                  weight={config.weight}
                  className={actionInProgress === action ? "animate-spin" : ""}
                />
              </button>
            );
          })}
          {!isTerminal && (
            <button
              className="ml-1 inline-flex size-7 cursor-pointer items-center justify-center rounded-md border border-kumo-fill bg-kumo-base text-kumo-default transition-colors hover:bg-kumo-fill disabled:cursor-not-allowed disabled:opacity-40"
              disabled={mutating || actionInProgress !== null || !mutation.ready}
                aria-busy={!mutation.ready}
              onClick={(e) => {
                e.stopPropagation();
                onSendEvent();
              }}
              title="Send Event"
              type="button"
            >
              <PaperPlaneTiltIcon size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export function WorkflowInstancesSurface({
  workspaceId,
  definition,
  active,
  onOpenInstance
}: WorkflowInstancesSurfaceProps) {
  const query = useWorkflowQuery({ view: "instances", workspaceId, active, className: definition.className, workerId: definition.routing.workerId, runtimeNamespaceId: definition.routing.runtimeNamespaceId, definitionVersion: definition.updatedAt });
  const instances = query.snapshot.catalog.instances.filter(instance => instance.className === definition.className), loaded = query.snapshot.loaded, error = query.snapshot.error, refreshing = query.snapshot.loading;
  const fetchInstances = () => query.dispatch({ _tag: "Refresh" }), handleRefresh = fetchInstances;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false), [sendEventTarget, setSendEventTarget] = useState<string | null>(null);
  useEffect(() => { setStatusFilter("all");setSendEventTarget(null); }, [query.selectionKey]);
  useEffect(() => { if (!active) { setDialogOpen(false);setSendEventTarget(null); } }, [active]);

  const statusCounts: Record<string, number> = {};
  for (const instance of instances) {
    statusCounts[instance.status] = (statusCounts[instance.status] ?? 0) + 1;
  }

  const sorted = [...instances].sort(
    (left, right) => right.createdAt - left.createdAt
  );
  const filtered =
    statusFilter === "all"
      ? sorted
      : sorted.filter((instance) => instance.status === statusFilter);

  return (
    <div className="h-full min-h-0 overflow-auto bg-kumo-base">
      <div className="px-6 py-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-kumo-default">
              {definition.className}
            </h2>
            <p className="truncate text-xs text-kumo-subtle">
              Workflow instances <code>{definition.file}</code>
            </p>
          </div>
        </div>

        <StatusSummary statusCounts={statusCounts} />

        <hr className="-mx-6 my-4 border-kumo-fill" />

        {/* Toolbar: filter (left), actions (right) */}
        <div className="mb-4 flex items-center justify-between">
          <DropdownMenu>
            <DropdownMenu.Trigger
              render={
                <button
                  type="button"
                  className="inline-flex h-9 min-w-36 cursor-pointer items-center justify-between rounded-lg border border-kumo-fill bg-kumo-base px-3 text-sm text-kumo-default transition-colors hover:bg-kumo-fill"
                >
                  <span>
                    {statusFilter === "all"
                      ? "All"
                      : (STATUS_SUMMARY_CONFIG.find(
                          (s) => s.key === statusFilter
                        )?.label ?? statusFilter)}
                  </span>
                  <CaretUpDownIcon size={14} className="text-kumo-subtle" />
                </button>
              }
            />
            <DropdownMenu.Content align="start" className="w-36" side="bottom">
              <DropdownMenu.Item
                className="cursor-pointer gap-2"
                icon={<ListIcon />}
                onClick={() => setStatusFilter("all")}
              >
                All
              </DropdownMenu.Item>
              {STATUS_SUMMARY_CONFIG.map(({ key, label, icon: Icon }) => (
                <DropdownMenu.Item
                  className="cursor-pointer gap-2"
                  icon={<Icon />}
                  key={key}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu>

          <div className="flex items-center gap-2">
            <Button
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                (e.target as HTMLButtonElement).blur();
                setDialogOpen(true);
              }}
              variant="primary"
            >
              <PlayIcon size={14} weight="fill" />
              Trigger
            </Button>
            <Button
              aria-label="Refresh instances"
              disabled={refreshing}
              onClick={() => void handleRefresh()}
              shape="square"
              variant="secondary"
            >
              <ArrowsClockwiseIcon
                size={18}
                className={refreshing ? "animate-spin" : ""}
              />
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-kumo-danger/20 bg-kumo-danger/8 p-4 text-kumo-danger">
            {error}
          </div>
        )}

        {!loaded ? (
          <div className="p-12 text-center text-kumo-subtle">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-kumo-fill bg-kumo-elevated px-5 py-8 text-center text-sm text-kumo-subtle">
            {statusFilter !== "all"
              ? `No instances found in state ‘${statusFilter}’`
              : "No instances found"}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-kumo-fill bg-kumo-base">
            {filtered.map((instance) => (
              <InstanceRow
                key={instance.id}
                instance={instance}
                workspaceId={workspaceId}
                onActionComplete={() => void fetchInstances()}
                onOpen={() => onOpenInstance(instance)}
                onSendEvent={() => setSendEventTarget(instance.id)}
                ownerKey={query.selectionKey}
                active={active}
              />
            ))}
          </div>
        )}
      </div>

      <CreateWorkflowInstanceDialog
        workspaceId={workspaceId}
        className={definition.className}
        expectedWorkerId={definition.routing.workerId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(instance) => {
          setDialogOpen(false);
          void fetchInstances();
          // A trigger completing after the surface left the foreground must
          // not focus-steal by opening the instance tab from the background.
          if (active) onOpenInstance(instance);
        }}
      />

      <WorkflowSendEventDialog
        workspaceId={workspaceId}
        instanceId={sendEventTarget ?? ""}
        open={sendEventTarget !== null}
        onOpenChange={(open) => {
          if (!open) setSendEventTarget(null);
        }}
        onSent={() => void fetchInstances()}
      />
    </div>
  );
}
